(() => {
  if (customElements.get('lm-country-size-selector')) return;

  // Verified against docs/size-guide/feishu-size-table.json, A1:K11 (2026-10-09).
  const lsmSizes = ['XS', 'S', 'M', 'L', 'XL', '0XL', '1XL', '2XL', '3XL', '4XL'];
  const internationalSizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
  const countries = {
    US: ['0', '2', '4', '6', '8', '10', '12', '14', '16', '18'],
    EU: ['32', '34', '36', '38', '40', '42', '44', '46', '48', '50'],
    UK: ['4', '6', '8', '10', '12', '14', '16', '18', '20', '22'],
    IT: ['36', '38', '40', '42', '44', '46', '48', '50', '52', '54'],
    AU: ['4', '6', '8', '10', '12', '14', '16', '18', '20', '22'],
    KR: internationalSizes,
    JP: internationalSizes,
    BR: ['PP', 'P', 'M', 'G', 'GG', 'XG', 'XGG', 'G1', 'G2', 'G3'],
    MX: ['0', '2', '4', '6', '8', '10', '12', '14', '16', '18']
  };
  const preferenceKey = 'lacemade:country-size';
  const normalize = (value) => String(value).trim().toUpperCase();

  function sizeIndexes(values, system) {
    const normalized = values.map(normalize);
    if (system === 'auto') {
      // 2XL alone could mean LSM 2XL or international 2XL: do not guess.
      const confirmedLsm = normalized.includes('0XL') || normalized.includes('1XL');
      if (!confirmedLsm && normalized.some((value) => !lsmSizes.slice(0, 5).includes(value))) return null;
      system = 'lsm';
    }
    const source = system === 'lsm' ? lsmSizes : system === 'international' ? internationalSizes : [];
    const indexes = normalized.map((value) => source.indexOf(value));
    return indexes.every((index) => index >= 0) ? indexes : null;
  }

  class CountrySizeSelector extends HTMLElement {
    connectedCallback() {
      if (this.root) return;
      this.root = this.closest('.product__block__variants');
      if (!this.root) return;
      this.country = 'US';
      try {
        const saved = localStorage.getItem(preferenceKey);
        if (Object.hasOwn(countries, saved)) this.country = saved;
      } catch (_) { /* Storage can be unavailable in private browsing. */ }
      this.onChange = (event) => {
        if (event.target.matches('[data-lm-country]') && this.contains(event.target)) {
          if (Object.hasOwn(countries, event.target.value)) {
            this.country = event.target.value;
            try { localStorage.setItem(preferenceKey, this.country); } catch (_) { /* Keep an in-page preference. */ }
          }
        }
        this.scheduleSync();
      };
      this.onClick = (event) => {
        const button = event.target.closest('[data-lm-size-value]');
        if (!button || !this.contains(button) || button.disabled) return;
        const control = this.controlFor(button.dataset.lmSizeValue);
        if (!control || this.unavailable(control)) return;
        // The original radio/popout owns variant ID, URL, price, SKU and images.
        if (control.tagName === 'OPTION') {
          control.parentElement.value = control.value;
          control.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          control.click();
        }
        this.scheduleSync();
      };
      this.onVariantChange = () => this.scheduleSync();
      this.root.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      document.addEventListener('theme:variant:change', this.onVariantChange);
      this.observer = new MutationObserver((records) => {
        const data = this.querySelector('[data-lm-size-values]');
        if (records.some((record) => !this.contains(record.target) || record.target === this || data?.contains(record.target))) {
          this.scheduleSync();
        }
      });
      this.observer.observe(this.root, { childList: true, attributes: true, characterData: true, subtree: true });
      this.scheduleSync();
    }

    disconnectedCallback() {
      this.root?.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('theme:variant:change', this.onVariantChange);
      this.observer?.disconnect();
      this.root = null;
    }

    scheduleSync() {
      if (this.syncScheduled) return;
      this.syncScheduled = true;
      queueMicrotask(() => {
        this.syncScheduled = false;
        if (!this.isConnected || !this.root) return;
        try { this.sync(); } catch (_) { if (!this.hidden) this.hidden = true; }
      });
    }

    originalSelector() {
      return this.root.querySelector('.selector-wrapper[data-option-position="' + this.dataset.optionPosition + '"]');
    }

    controlFor(value) {
      const selector = this.originalSelector();
      if (!selector) return null;
      return [...selector.querySelectorAll('input[type="radio"], [data-popout-option], select option')]
        .find((control) => (control.dataset.value ?? control.value) === value);
    }

    unavailable(control) {
      return control.disabled || control.classList.contains('unavailable') || control.classList.contains('sold-out');
    }

    sync() {
      const values = JSON.parse(this.querySelector('[data-lm-size-values]').textContent);
      const indexes = sizeIndexes(values, normalize(this.dataset.sizeSystem || 'auto').toLowerCase());
      const original = this.originalSelector();
      if (!indexes || !values.length || !original || values.some((value) => !this.controlFor(value))) {
        if (!this.hidden) this.hidden = true;
        return;
      }
      const selected = original.querySelector('input[type="radio"]:checked')?.value
        ?? original.querySelector('[data-popout-option][aria-current="true"]')?.dataset.value
        ?? original.querySelector('select, [data-single-option-selector]')?.value;
      const country = this.country;
      const row = this.querySelector('[data-lm-country-buttons]');
      const signature = JSON.stringify([values, indexes, country]);
      if (this.signature !== signature || row.children.length !== values.length) {
        const focused = row.contains(document.activeElement) ? document.activeElement.dataset.lmSizeValue : null;
        const buttons = values.map((value, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'lm-country-sizes__button';
          button.dataset.lmSizeValue = value;
          button.textContent = countries[country][indexes[index]];
          return button;
        });
        row.replaceChildren(...buttons);
        this.signature = signature;
        if (focused !== null) buttons.find((button) => button.dataset.lmSizeValue === focused)?.focus();
      }
      this.querySelector('[data-lm-country]').value = country;
      this.querySelector('[data-lm-country-label]').textContent = country + ' Size';
      row.setAttribute('aria-label', country + ' sizes');
      [...row.children].forEach((button, index) => {
        const control = this.controlFor(values[index]);
        const unavailable = this.unavailable(control);
        const status = control.classList.contains('unavailable') ? ', unavailable' : control.classList.contains('sold-out') ? ', sold out' : '';
        button.disabled = unavailable;
        button.classList.toggle('is-unavailable', unavailable);
        button.setAttribute('aria-pressed', String(selected === values[index]));
        button.setAttribute('aria-label', country + ' size ' + button.textContent + ', LaceMade ' + values[index] + status);
      });
      if (this.hidden) this.hidden = false;
    }
  }

  customElements.define('lm-country-size-selector', CountrySizeSelector);
})();
